import React, { useEffect, useState } from 'react';
import ShareButton from './ShareButton.js';
import { useAdminAuth } from '../../../context/AdminAuthContext';
import '../../css/TalkCard.css';
import ReactLinkify from 'react-linkify';
import LinkifyText from './LinkifyText.js';
import { Link } from "react-router-dom";
import { API_BASE_URL } from '../scripts/control';
import { ROUTES, ROUTE_PATHS } from '../../../config/constants';
import ImageCrop from "../components/ImageCrop";
import RichTextInput from '../components/RichTextInput';
import { linkifyContent } from '../../../utils/linkifyHtml';
import { dataURLtoBlob } from '../../../utils/dataURLtoBlob';
import OptimizedImage from '../../../components/OptimizedImage';


const TalkCard = () => {
    const [talks, setTalks] = useState([]);
    const [expanded, setExpanded] = useState({});
    const [showConfirmCloseModal, setShowConfirmCloseModal] = useState(false);
    const [editingTalk, setEditingTalk] = useState(null);
    const [deletingTalk, setDeletingTalk] = useState(null);
    const { isAdmin } = useAdminAuth();
    // Para adaptar imágenes
    const [showCropModal, setShowCropModal] = useState(false); // modal de crop
    const [selectedImage, setSelectedImage] = useState(null);  // imagen original (DataURL)
    const [croppedImage, setCroppedImage] = useState(null); 
    const [bodyContent, setBodyContent] = useState('');
    const [locationContent, setLocationContent] = useState('');


    // Estado para la paginación
    const [currentPage, setCurrentPage] = useState(1);
    const talksPerPage = 5;
    const pageLimit = 5; // Páginas visibles a la vez

    useEffect(() => {
        fetch(`${API_BASE_URL}/api/talk_func.php`) 
            .then(response => response.json())
            .then(data => {
                if (data.error) {
                } else {
                    setTalks(data);
                    setExpanded(data.reduce((acc, talk) => ({ ...acc, [talk.idtalk]: false }), {}));
                }
            })
            .catch(() => {});
    }, []);

    const handleToggle = (id) => {
        setExpanded(prevState => ({ ...prevState, [id]: !prevState[id] }));
    };

    const handleEdit = (talk) => {
        setEditingTalk(talk);
        setBodyContent(talk.body);
        setSelectedImage(null);
        setCroppedImage(null);
    };

    const handleDelete = (talk) => {
        setDeletingTalk(talk);
    };

    const confirmDelete = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/delete_talk.php`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ id: deletingTalk.idtalk }),
            });

            if (response.ok) {
                setTalks(talks.filter(talk => talk.idtalk !== deletingTalk.idtalk));
                setDeletingTalk(null);
            } else {
            }
        } catch (error) {
        }
    };

    const cancelDelete = () => {
        setDeletingTalk(null);
    };

    const handleCloseModal = () => {
        setShowConfirmCloseModal(true);
    };

    const handleConfirmClose = () => {
        setEditingTalk(null);
        setShowConfirmCloseModal(false);
        setSelectedImage(null);
        setCroppedImage(null);
    };

    const handleCancelClose = () => {
        setShowConfirmCloseModal(false);
    };

    const handleSave = async () => {
        const formData = new FormData();
        const title = document.getElementById('title').value;
        const body = bodyContent;
        const imgFile = document.getElementById('img_url').files[0];
        const imgAlt = document.getElementById('img_alt').value;
        const day = document.getElementById('day').value;
        const hour = document.getElementById('hour').value;
        const location = locationContent;

        formData.append('id', editingTalk.idtalk);
        formData.append('title', title);
        formData.append('body', body);
        formData.append('day', day);
        formData.append('hour', hour);
        formData.append('location', location);
        formData.append('img_alt', imgAlt);
        let savedImgFilename = editingTalk.img_url;
        if (croppedImage) {
          const { blob, ext, isValid } = dataURLtoBlob(croppedImage);
          const filename = imgFile?.name || `img_${Date.now()}.${ext}`;
          if (isValid && blob.size > 0) {
            formData.append('img_url', blob, filename);
            savedImgFilename = filename;
          } else {
            console.warn('Conversión de imagen recortada falló, usando archivo original');
            const file = document.getElementById('img_url').files[0];
            if (file) {
              formData.append('img_url', file);
              savedImgFilename = file.name;
            }
          }
        } else {
          const file = document.getElementById('img_url').files[0];
          if (file) {
            formData.append('img_url', file);
            savedImgFilename = file.name;
          }
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/update_talk.php`, {
                method: 'POST',
                body: formData,
                credentials: 'include',
            });

            const data = await response.json().catch(() => ({}));
            if (response.ok && data.success) {
                const finalImgUrl = data.img_url != null ? data.img_url : savedImgFilename;
                const updatedTalks = talks.map(talk =>
                    talk.idtalk === editingTalk.idtalk
                        ? {
                              ...talk,
                              title,
                              body,
                              day,
                              hour,
                              location,
                              img_url: finalImgUrl,
                              imgAlt,
                          }
                        : talk
                );
                setTalks(updatedTalks);
                setEditingTalk(null);
                setSelectedImage(null);
                setCroppedImage(null);
            } else {
                alert('Error al actualizar el congreso/seminario.');
            }
        } catch (error) {
            alert('Error al actualizar el congreso/seminario: ' + (error?.message || 'Error desconocido'));
        }
    };

    const formatDate = (dateString) => {
        const options = {
            year: '2-digit',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        };
        return new Date(dateString).toLocaleString('es-ES', options);
    };

    // Crear URL para agregar evento a Google Calendar
    const createGoogleCalendarUrl = (talk) => {
        const formatDateForGoogle = (dateString, timeString) => {
            if (!dateString || !timeString) {
                return null;
            }

            const dateTimeString = `${dateString}T${timeString}`;  // Formato: YYYY-MM-DDTHH:MM:SS
            const parsedDate = new Date(dateTimeString);

            if (isNaN(parsedDate.getTime())) {
                return null;
            }

            return parsedDate.toISOString().replace(/[-:.]/g, '').slice(0, 15) + 'Z';
        };

        const startDate = formatDateForGoogle(talk.day, talk.hour);
        const location = talk.location || 'Sin ubicación especificada';

        if (!startDate) {
            return '#';
        }

        return `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
            talk.title
        )}&dates=${startDate}/${startDate}&location=${encodeURIComponent(location)}&sf=true&output=xml`;
    };

    const truncateText = (text, maxLength) => {
        if (text.length > maxLength) {
            return text.slice(0, maxLength) + '...';
        }
        return text;
    };

    const indexOfLastTalk = currentPage * talksPerPage;
    const indexOfFirstTalk = indexOfLastTalk - talksPerPage;
    const currentTalks = talks.slice(indexOfFirstTalk, indexOfLastTalk);

    const totalPages = Math.ceil(talks.length / talksPerPage);

    const startPage = Math.max(1, currentPage - Math.floor(pageLimit / 2));
    const endPage = Math.min(totalPages, startPage + pageLimit - 1);
    const pageNumbers = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const nextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const prevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    // ------------------- Manejo de imagen en la edición (crop) -------------------
    const handleEditImageChange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
    
      // Convertimos en DataURL
      const reader = new FileReader();
      reader.onload = () => {
        setSelectedImage(reader.result); // guardamos la imagen original
        setShowCropModal(true);         // abrimos el modal de recorte
      };
      reader.readAsDataURL(file);
    };
    
    const handleCropSave = (cropped) => {
      setCroppedImage(cropped);
      setShowCropModal(false);
    };


    return (
        <div className="container-fluid mb-4">
            <div className="row">
                {currentTalks.map((talk) => {
                    const imgSrc = talk.img_url
                        ? `${API_BASE_URL}/public/uploads/images/${talk.img_url}`
                        : `${API_BASE_URL}/public/uploads/images/comic.jpg`;               
                       
                return (
                    <div className="col-md-4 mb-4" key={talk.idtalk}>
                    <div
                        className={`card ${expanded[talk.idtalk] ? 'expanded' : ''}`}
                        id={`card-${talk.idtalk}`}
                    >
                        <h5 className="card-title">{talk.title}</h5>
                        <OptimizedImage src={imgSrc} alt={talk.img_alt || 'Imagen por defecto'} loading="lazy" aspectRatio={16/9} />
                        <div className="card-body ms-1">
                            <small className="text-muted mt-3">Última actualización: {new Date(talk.date).toLocaleDateString()}</small><br />
                            <div
                              className="article-preview"
                              dangerouslySetInnerHTML={{
                               __html: linkifyContent(talk.body)
                              }}
                            />
                            <br />


                            <Link to={ROUTE_PATHS.DETAIL_LINK.TALK(talk.idtalk)}>
                                <button
                                        style={{
                                            marginRight: '10px',
                                            marginBottom: '10px',
                                            padding: '5px 10px',
                                            backgroundColor: '#005EC4',
                                            border: 'none',
                                            borderRadius: '5px',
                                            cursor: 'pointer',
                                        }}
                                >
                                Ver detalle
                                </button>
                            </Link>
                            {talk.location && talk.day && talk.hour &&(<a
                                href={createGoogleCalendarUrl(talk)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-primary btn-calendar">
                                <i className="fa-brands fa-google"></i> Agendar
                            </a>)}
                            {isAdmin && (
                                <div style={{ marginTop: '10px' }}>
                                    <button
                                        onClick={() => handleEdit(talk)}
                                        style={{
                                            marginRight: '10px',
                                            padding: '5px 10px',
                                            backgroundColor: '#ffc107',
                                            border: 'none',
                                            borderRadius: '5px',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        Editar
                                    </button>
                                    <button
                                        onClick={() => handleDelete(talk)}
                                        style={{
                                            padding: '5px 10px',
                                            backgroundColor: '#dc3545',
                                            border: 'none',
                                            borderRadius: '5px',
                                            cursor: 'pointer',
                                            color: 'white',
                                        }}
                                    >
                                        Borrar
                                    </button>
                                </div>
                            )}
                        </div>
                        <ShareButton
                            shareUrl={`${API_BASE_URL}/detalles/congreso/${talk.idtalk}`}
                            title={`Mira este congreso: ${talk.title}`}
                        />
                        <br />
                        <hr />
                        </div>
                    </div>
                );
            })}
        </div>

            {/* Paginación */}
            {talks.length > talksPerPage && (
                <div className="pagination">
                    <button
                        className="back"
                        onClick={prevPage}
                        disabled={currentPage === 1}
                    >
                        Anterior
                    </button>

                    {pageNumbers.map((pageNumber) => (
                        <button
                            key={pageNumber}
                            onClick={() => paginate(pageNumber)}
                            className={`pagination-button ${currentPage === pageNumber ? 'active' : ''}`}
                            style={{
                                backgroundColor: currentPage === pageNumber ? '#007bff' : '',
                                color: currentPage === pageNumber ? 'white' : '',
                            }}
                        >
                            {pageNumber}
                        </button>
                    ))}

                    <button
                        className="next"
                        onClick={nextPage}
                        disabled={currentPage === totalPages}
                    >
                        Siguiente
                    </button>
                </div>
            )}

            {/* Modal para editar el congreso/seminario */}
            {editingTalk && (
                <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Editar congreso/seminario</h5>
                                <button type="button" className="btn-close" aria-label="Close" onClick={handleCloseModal}></button>
                            </div>
                            <div className="modal-body">
                                <form>
                                    <label htmlFor="title">Título del congreso/seminario:</label><br />
                                    <input type="text" id="title" name="title" defaultValue={editingTalk.title} /><br />
                                    <span id="body-label-talk-edit">Texto principal:</span><br />
                                    <RichTextInput
                                        id="body-talk-edit"
                                        ariaLabelledBy="body-label-talk-edit"
                                        initialContent={bodyContent}
                                        onChange={(content) => setBodyContent(content)}
                                        placeholder="Pega o escribe aquí el contenido con formato..."
                                        style={{ minHeight: '150px', direction: 'ltr' }}
                                    />
                                    <label>Imagen del congreso/seminario:</label><br />
                                    <input
                                      type="file"
                                      id="img_url"
                                      name="img_url"
                                      accept="image/*"
                                      onChange={handleEditImageChange}
                                    />
                                    {/* Previsualización de la imagen */}
                                    {croppedImage ? (
                                      <div>
                                        <p>Previsualización (Imagen recortada):</p>
                                        <img
                                          src={croppedImage}
                                          alt="Imagen recortada"
                                          style={{ maxWidth: '100%' }}
                                        />
                                      </div>
                                    ) : selectedImage ? (
                                      <div>
                                        <p>Previsualización (Sin recortar):</p>
                                        <img
                                          src={selectedImage}
                                          alt="Imagen seleccionada"
                                          style={{ maxWidth: '100%' }}
                                        />
                                      </div>
                                    ) : null}<br />
                                    <label htmlFor="img_alt">Texto alternativo de la imagen:</label><br />
                                    <input type="text" id="img_alt" name="img_alt" defaultValue={editingTalk.img_alt} /><br />
                                    <small>necesario para personas con discapacidad visual</small>
                                    <small>Rellenar a continuación para permitir agendar con Google Calendar:</small><br />
                                    <label htmlFor="day">Día del evento:</label><br />
                                    <small className="text-muted"> Se ordenará por este parámetro</small><br />
                                    <input type="date" id="day" name="day" defaultValue={editingTalk.day} /><br />
                                    <label htmlFor="hour">Hora de inicio del evento:</label><br />
                                    <input type="time" id="hour" name="hour" defaultValue={editingTalk.hour} /><br /><br />
                                    <label htmlFor="location">Lugar del evento:</label><br />
                                    <small>Es importante incluir aquí detalles como número de la calle y cp, para que google maps pueda localizarlo.</small><br />
                                    <RichTextInput id="location" initialContent={locationContent} onChange={setLocationContent} placeholder="Lugar..." editorKey={`talk-edit-${editingTalk?.idtalk}-loc`} style={{ minHeight: '60px' }} /><br />  
                                </form>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>Cancelar</button>
                                <button type="button" className="btn btn-primary" onClick={handleSave}>Guardar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de confirmación de cierre */}
            {showConfirmCloseModal && (
                <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Confirmar cierre</h5>
                                <button type="button" className="btn-close" aria-label="Close" onClick={handleCancelClose}></button>
                            </div>
                            <div className="modal-body">
                                <p>¿Quieres abandonar la edición? Se perderán los cambios no guardados.</p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={handleCancelClose}>Volver</button>
                                <button type="button" className="btn btn-primary" onClick={handleConfirmClose}>Salir</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de confirmación de borrado */}
            {deletingTalk && (
                <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Confirmar borrado</h5>
                                <button type="button" className="btn-close" aria-label="Close" onClick={cancelDelete}></button>
                            </div>
                            <div className="modal-body">
                                <p>¿Estás seguro de que deseas borrar el congreso/seminario {deletingTalk.title}?</p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={cancelDelete}>Cancelar</button>
                                <button type="button" className="btn btn-danger" onClick={confirmDelete}>Borrar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        {/* Modal de crop (se abre cuando subimos una imagen en la edición) */}
            {showCropModal && (
              <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
                <div className="modal-dialog" style={{ maxWidth: '90%' }}>
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">Recortar imagen</h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setShowCropModal(false)}
                      ></button>
                    </div>
                    <div className="modal-body">
                      <ImageCrop image={selectedImage} onImageSave={handleCropSave} />
                    </div>
                  </div>
                </div>
              </div>
            )}
        </div>
    );
};

export default TalkCard;